import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent from '../../../hooks/usePageContent';
import { testimonialsForCity, colorForIndex } from './testimonialPool';
import styles from './Testimonials.module.postcss';
import tripleStyles from './Triple.module.postcss';
import CDN_BASE from '../../../utils/cdn';

// Testimonials — sitewide customer quotes block, rendered on every
// catering / services / cuisine landing page (~20 consumers).
//
// Reads from the `shared_testimonials` CMS slug so admins can swap the quotes
// without redeploying. When no CMS row exists (or the row has no cards), it
// renders the city's slice of the real-customer pool in `testimonialPool.js`
// — each of the three sites gets its own four quotes so the block is never
// duplicate content across cities.
//
// A card with no photo renders an initials avatar in the coloured tile, so
// real quotes can ship without a portrait.

const MAX_CARDS = 4;

// Resolve a CMS image value to a full URL. Three accepted shapes:
//   1. Full URL (`https://...`) — used verbatim
//   2. Host-relative path (`/static/...`) — used verbatim
//   3. CDN-relative path (`photos/Brett.jpeg` or `cms_pages/foo.jpg`) — prefixed with CDN_BASE
// Returns null when empty so the card falls back to an initials avatar.
function resolveImage(value) {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('/')) return value;
  return `${CDN_BASE}/${value}`;
}

// "Kyle & Caroline - Kent, WA" -> "KC"; "Sarah M." -> "SM"
function initialsFor(attribution) {
  if (!attribution) return '';
  const namePart = attribution.split(/\s[-–—]\s/)[0];
  const words = namePart.split(/[\s&]+/).filter((w) => /^[A-Za-z]/.test(w));
  return words.slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}

function cardsFromCms(cms) {
  if (!cms) return [];
  const cards = [];
  for (let i = 1; i <= MAX_CARDS; i += 1) {
    const card = cms[`card${i}`];
    if (card && card.body) {
      cards.push({
        image: resolveImage(card.image),
        alt: card.alt || card.attribution || '',
        body: card.body,
        attribution: card.attribution || '',
        color: card.color || colorForIndex(cards.length),
      });
    }
  }
  return cards;
}

function Testimonials({ title }) {
  const config = useContext(ConfigContext) || {};
  const { content: cms } = usePageContent('shared_testimonials');

  const cmsCards = cardsFromCms(cms);
  const cards = cmsCards.length > 0 ? cmsCards : testimonialsForCity(config.city_name);
  const heading = cms?.heading || title || "Join Thousands Who've Booked Catering With Us!";

  return (
    <section className='Section--extraPadding'>
      <h3 className='u-phosphate u-mb2'>{heading}</h3>
      <div
        className={cn(tripleStyles.Triple_wrapper, {
          [tripleStyles.Triple_wrapper__quad]: cards.length === 4,
        })}
      >

        {cards.map((card) => (
          <div key={`${card.attribution}|${card.body.slice(0, 24)}`} className={tripleStyles.Triple}>
            <div className={cn(styles.Testimonial, styles[`Testimonial__${card.color}`])}>
              {card.image ? (
                <img
                  src={card.image}
                  alt={card.alt}
                  className={styles.Testimonial_Img}
                  loading='lazy'
                  fetchPriority='low'
                />
              ) : (
                <div className={styles.Testimonial_Initials} aria-hidden='true'>
                  {initialsFor(card.attribution)}
                </div>
              )}
            </div>
            <p className='u-mt1'>{card.body}</p>
            <h3 className='u-mt1 u-mb0'>{card.attribution}</h3>
          </div>
        ))}
      </div>
    </section>
  );
}

Testimonials.propTypes = {
  title: PropTypes.string,
};

Testimonials.defaultProps = {
  title: null,
};

export default Testimonials;
